/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Sunday, September 11th 2022, 10:03:53 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import { NextActivation } from "lib/schemas";
import { useListEditorContext } from "lib/providers";
import { useDeleteNextActivation } from "service-builder/services/OutgoingBranchesConfigurationAPI";
import { DeleteOSItemDialog } from "version-control/components/DeleteOSItemDialog";
import { OSActionButton } from "ui-widgets";

interface ActivationBranchListItemProps {
  activationBranch: NextActivation;
  onClickEditActivationBranch: (activationBranchId: NextActivation["id"]) => void;
  onDeleteActivationBranchCallback: (deletedActivationBranch: NextActivation) => void;
}

export const ActivationBranchListItem = (props: ActivationBranchListItemProps) => {
  const { handleEditActivationBranch, onDeleteActivationBranch, updateActivationBranchList } =
    useActivationBranchListItem(props);

  const { activationBranch } = props;

  let listItemTextPrefix: string = "";
  let listItemTextSuffix: string = "";

  switch (activationBranch.conditions_count) {
    case 0:
      listItemTextPrefix = "Always activate  ";
      break;
    default:
      listItemTextPrefix = "Activate ";
      listItemTextSuffix = `when ${activationBranch.condition_logic_type} of ${activationBranch.conditions_count} ${
        activationBranch.conditions_count === 1 ? "condition is" : "conditions are"
      } met.`;
  }

  return (
    <Paper elevation={8}>
      <ListItem alignItems="flex-start">
        <ListItemAvatar>
          <AccountTreeIcon color="secondary" />
        </ListItemAvatar>
        <ListItemText
          primary={activationBranch.description}
          secondary={
            <Stack direction="row" sx={{ justifyContent: "space-between" }}>
              <div>
                <Typography sx={{ display: "inline" }} component="span" variant="subtitle2" color="text.primary">
                  {listItemTextPrefix}
                </Typography>
                <Typography sx={{ display: "inline" }} component="span" variant="subtitle1" color="primary">
                  {activationBranch.ontrue_task_definition_title}{" "}
                </Typography>
                {listItemTextSuffix}
              </div>
              <Stack direction="row" spacing={2}>
                <OSActionButton actionStyle="edit" onClick={handleEditActivationBranch}>
                  Edit
                </OSActionButton>
                <DeleteOSItemDialog<NextActivation>
                  useDeleteOSItem={useDeleteNextActivation}
                  itemId={activationBranch.id}
                  onDelete={onDeleteActivationBranch}
                  onRestore={updateActivationBranchList}
                >
                  {({ handleOpen }) => (
                    <OSActionButton onClick={handleOpen} actionStyle="delete">
                      Delete
                    </OSActionButton>
                  )}
                </DeleteOSItemDialog>
              </Stack>
            </Stack>
          }
        />
      </ListItem>
    </Paper>
  );
};

const useActivationBranchListItem = ({
  activationBranch,
  onClickEditActivationBranch,
  onDeleteActivationBranchCallback,
}: ActivationBranchListItemProps) => {
  const { removeItem: removeActivationBranch, updateListAndSelectIfNewItem: updateActivationBranchList } =
    useListEditorContext<NextActivation>();

  return {
    updateActivationBranchList,
    handleEditActivationBranch: () => onClickEditActivationBranch(activationBranch.id),
    onDeleteActivationBranch: () => {
      removeActivationBranch(activationBranch.id);
      onDeleteActivationBranchCallback(activationBranch);
    },
  };
};
